import { Component, OnInit, Inject, OnDestroy, DOCUMENT } from '@angular/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionType, InteractionStatus, PopupRequest, RedirectRequest, AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { PaymentStatusDto } from '@api-client';


import { AuthenticationService } from './auth/authentication.service';
import { decodeJwtPayload, zeymoUserId } from './auth/auth-mode';
import { EnvConfigLoaderService } from './shared/services/app';
import { DialogExtendedService } from './shared/services/dialog-extended.service';
import { HttpErrorHandlerService } from './shared/services/error';
import { DataHelperService } from './shared/services/data-helper';
import { User } from './shared/models';
import { PaymentStatusOverrideStore } from './core/payment-popup/payment-status-override.store';
import { PaymentStatusPollService, pickPaymentStatus } from './core/payment-popup/payment-status-poll.service';
import { PermissionStore } from './shared/services/permission/permission-store.service';
import { ChunkLoadRecoveryService } from './shared/services/chunk-load-recovery/chunk-load-recovery.service';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  isIframe = false;
  isAuthenticated = false;
  isLoading = false;
  isUserDataLoaded = false; // To Load the userApp component once
  // Settings bring the status once at startup; the poll keeps it current so ZeymoAdmin changes reach open tabs (ZEY-3061).
  paymentStatus$: Observable<PaymentStatusDto | null | undefined> = combineLatest([
    this.dataHelperService.listsLoaded.pipe(map((result) => result?.paymentStatus)),
    this.paymentStatusPoll.latest$,
    this.paymentStatusOverride.override$
  ]).pipe(map(([fromSettings, polled, override]) => pickPaymentStatus(fromSettings, polled, override)));
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    @Inject(DOCUMENT) private document: Document,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private authService: AuthenticationService,
    private dataHelperService: DataHelperService,
    private env: EnvConfigLoaderService,
    private paymentStatusOverride: PaymentStatusOverrideStore,
    private paymentStatusPoll: PaymentStatusPollService,
    private permissionStore: PermissionStore,
    private chunkLoadRecovery: ChunkLoadRecoveryService,
    private errorService: HttpErrorHandlerService,
    private dialogExtendedService: DialogExtendedService
  ) {}

  ngOnInit(): void {
    // Recover from stale lazy chunks after a redeploy (ZEY-789)
    this.chunkLoadRecovery.init();

    // Start re-reading the payment status once the signed-in shell has its settings (ZEY-3061).
    this.dataHelperService.listsLoaded
      .pipe(filter((result) => !!result), take(1), takeUntil(this._destroying$))
      .subscribe(() => this.paymentStatusPoll.start());

    this.isIframe = window !== window.parent && !window.opener;

    // A refused write used to be surfaced only where the call site thought to catch a 403, so
    // anywhere else it left the screen unchanged — Deliver looked like it did nothing (ZEY-2418).
    this.errorService.forbiddenError$
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => this.dialogExtendedService.showNotAuthorizedDialog());

    this.msalService.handleRedirectObservable().subscribe({
      error: (error) => console.log(error)
    });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
            msg.eventType === EventType.INITIALIZE_START
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        // Only show loading spinner during initial app load, not during background token refresh
        if (result.eventType === EventType.INITIALIZE_START) {
          this.isLoading = true;
        }
        // console.log("EventType: "+result.eventType);
        const payload = result.payload as AuthenticationResult;
        if (
          result.eventType === EventType.LOGIN_SUCCESS ||
          result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ) {
          this.isLoading = false;
          this.msalService.instance.setActiveAccount(payload.account);
          this.rememberApiTokenClaims(payload);
          this.getClaims(payload.account);
        }

        if (result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
          this.isLoading = false;
        }
      });

    /**
     * You can subscribe to MSAL events as shown below. For more info,
     * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
     */
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe((result: InteractionStatus) => {
        // console.log("inProgress: " + result);
        if (result === InteractionStatus.None) {
          this.checkAndSetActiveAccount();
          this.dataHelperService.preload();
        }

      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    // Set authenticated if there's an active account (existing or just set)
    if (this.msalService.instance.getActiveAccount()) {
      this.isAuthenticated = true;
    }
  }

  /** Keeps the Zeymo claims of a Zeymo API access token; under External ID they live nowhere else (ZEY-3138). */
  private rememberApiTokenClaims(result: AuthenticationResult): void {
    const claims = decodeJwtPayload(result?.accessToken);
    if (claims?.['extension_TenantId']) {
      this.authService.setApiTokenClaims(claims);
    }
  }

  getClaims(claims: any) {
    const extid = this.env.getSettings('authMode') === 'extid';
    const apiClaims = this.authService.getApiTokenClaims();
    const tenantId = claims?.idTokenClaims?.extension_TenantId ?? (extid ? apiClaims?.['extension_TenantId'] : null);
    if (extid && !tenantId) {
      // A Graph token so far; the first Zeymo API token carries the tenant.
      return;
    }
    const user: User = {
      name: claims ? (claims['name'] ?? (extid ? apiClaims?.['name'] : null)) : null,
      oid: extid ? zeymoUserId(apiClaims, claims?.idTokenClaims, claims?.localAccountId) : (claims ? claims['oid'] : null),
      jobTitle: claims ? claims['jobTitle'] : null,
      city: claims ? claims['city'] : null,
      username: claims ? claims['username'] : null,
      tenantId: claims ? tenantId : null
    };
    this.authService.sendUser(user);
    this.permissionStore.load();
    this.isAuthenticated = true;
    this.isUserDataLoaded = true;
  }

  async login(userFlowRequest?: RedirectRequest | PopupRequest) {

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {

      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginPopup({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalService.instance.setActiveAccount(response.account);
          });
      } else {
        this.msalService.loginPopup(userFlowRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalService.instance.setActiveAccount(response.account);
          });
      }

    } else {

      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
      } else {
        this.msalService.loginRedirect(userFlowRequest);
      }

    }
  }
  
  ngOnDestroy(): void {
    this.paymentStatusPoll.stop();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}